import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { registerClientActionServices } from "@cervval.js/client-action/src/utils/client.registerService";
import { registerDefaultServices } from "@cervval.js/client-html5-spa";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { buildObjectMap } from "@cervval.js/common/src/tools/collection.map.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CapacitorService } from "@ehealth_tools/client-capacitor/src/services/capacitor.service";
import { WebSocketRefreshService } from "@ehealth_tools/client-security/src/services/websocketRefresh.service";
import { BookingProfessional } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { isProfessional } from "@ehealth_tools/common-booking/src/data/booking.tools";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { PRODUCT_TYPE } from "@ehealth_tools/common-payment/src/data/product.types";
import { APPLICATION_ENDPOINT } from "@sportsante/common-shared/src/const";
import { getDTOList } from "@sportsante/common-shared/src/data/global/cacheService.tools";
import { FetchDataTypes, FetchDataUID } from "@sportsante/common-shared/src/data/global/fetchData.config";
import { elementTypeNames, ElementTypes, getDbDefinition, registerDTOModelService } from "@sportsante/common-shared/src/data/global/global.model";
import { ProductDTO } from "@sportsante/common-shared/src/data/payments/product.dto";
import { AppLoginData } from "@sportsante/common-shared/src/data/users/appLoginData";
import { UserDTO } from "@sportsante/common-shared/src/data/users/user.dto";
import { UserHelper } from "@sportsante/common-shared/src/data/users/user.helper";
import { ClientBookingService } from "../../shared/services/clientBookingService";
import { ActivmedBookingCaptcha } from "./booking.googleReCaptcha.service";
import { AppClientPermissionService } from "./client.permission.service";
import { AppDataSourceService } from "./dataSource.service";

/**
 * Register application services on injector
 */
export function registerServices(): void {
    // register single page applications default services
    registerDefaultServices();

    injector.register(new WebSocketRefreshService(), { overrideIfDependencyRegistered: true });
    const capacitorService = new CapacitorService<AppLoginData, ElementTypes, FetchDataTypes>({
        fetchOptions: { method: FetchDataUID.GET_ALL, variables: undefined },
        routesServiceOptions: {
            urlPrefix: APPLICATION_ENDPOINT
        }
    });
    injector.register(capacitorService);

    injector.register(new ActivmedBookingCaptcha());


    // register application core services
    registerDTOModelService();

    const cacheService = new ClientActionCacheService<ElementTypes, unknown>(buildObjectMap(getDbDefinition().tables, "name"));
    cacheService.registerDTOCacheRepositories(elementTypeNames);
    // TODO set cacheService.dirtyCacheHandler
    registerClientActionServices(cacheService);

    /** Override fetch DTOs to flag __tableName*/
    const superFetchDataIfNeeded = cacheService.fetchDataIfNeeded.bind(cacheService);
    cacheService.fetchDataIfNeeded = async (uid, variables, tables) => {
        // Normal Fetch
        const fetchResult: { fetchedTables: (keyof ElementTypes)[] } = { fetchedTables: [] };
        await superFetchDataIfNeeded(uid, variables, tables)
            .then(result => fetchResult.fetchedTables = result.fetchedTables)
            .catch(e => getLogConsole().error(e));

        for (const table of fetchResult.fetchedTables) {
            // Sort dtoList by Id
            getDTOList(table).sort((a, b) => a.id - b.id);
            //
            const tableDTOs = getDTOList(table);
            for (const dto of tableDTOs) {
                // Flag __tableName
                dto.__tableName = table;
            }
        }
        return fetchResult;
    };

    injector.register(new AppClientPermissionService());

    injector.register(new ClientBookingService({
        tables: {
            bookingSessions: "booking_sessions",
            bookingUsers: "user_profiles",
            bookingCompanies: "companies",
            bookingCalendars: "booking_calendars",
            bookingExceptions: "booking_exceptions",
            bookingWeeks: "booking_weeks",
            bookingProducts: "products",
            bookingUserSessions: "booking_user_sessions",
            bookingSharing: "booking_sharings",
            bookingUserSpecialty: "users_specialties",
            bookingProfessionalCompany: "company_affectations"
        },
        getData: (table) => injector.get<ClientActionCacheService<ElementTypes, unknown>>(ACTION_CACHE_SERVICE).getDTOCacheRepository(table).dtoList as anyReallyAny, // TODO typing error
        getById: (table, id) => injector.get<ClientActionCacheService<ElementTypes, unknown>>(ACTION_CACHE_SERVICE).getDTOCacheRepository(table).getDTOById(id) as anyReallyAny,
        dbDefinitions: getDbDefinition(),
        isMember: (pro) => {
            return pro.consentTs != null;
        },
        isProfessionalCompanyRelationShip: (proCompany) => {
            return UserHelper.isProfessional(proCompany);
        }
    }, {
        initializers: {
            product: () => {
                const self = injector.get<ClientBookingService>(BOOKING_SERVICE);
                const currentUser = self.getCurrentUser();
                return Promise.resolve(<ProductDTO>{
                    ownerCompanyId: isProfessional<BookingProfessional & UserDTO>(currentUser) ? self.getCompany(currentUser)?.id : undefined,
                    isPublic: false,
                    type: PRODUCT_TYPE.SERVICE
                });
            }
        }
    }));

    // Datasource service
    injector.register(new AppDataSourceService, { overrideIfDependencyRegistered: true });

    // register modules services

}